import React, { useState, useEffect, useCallback } from 'react';
import './UsersPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const ROLE_OPTIONS = [
  { value: 'client', label: 'Client (read-only)' },
  { value: 'biller', label: 'Biller (full access)' },
  { value: 'admin', label: 'Admin (all practices)' },
];

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  biller: 'Biller',
  client: 'Client',
};

// Text columns sort on the text the row actually shows, so what you see is what
// you sort by. Last Seen is the documented exception: it carries type 'number'
// and sorts on the raw epoch, because sorting a formatted date as text orders it
// Apr, Aug, Dec, Feb. Any future non-text column needs the same tag — the
// comparator below branches on it. Actions is the only column left out.
//
// User holds the name and the email in one cell. That cost a sort key: they were
// two columns and sorting by email is gone, so the column sorts by the name it
// shows first, falling back to the email for a row that has no name yet.
const SORT_COLUMNS = [
  { key: 'user', label: 'User' },
  { key: 'role', label: 'Role' },
  { key: 'practices', label: 'Practices' },
  { key: 'status', label: 'Status' },
  { key: 'last_seen', label: 'Last Seen', type: 'number' },
];

const STATUS_OPTIONS = ['Active', 'Invited', 'Inactive'];

function UsersPage({ practices }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  // Last Seen comes from Clerk, not our DB, so it can be missing while every
  // other column is current. Tracked separately because a null timestamp alone
  // cannot say whether the user was never active or Clerk was unreachable.
  const [lastSeenAvailable, setLastSeenAvailable] = useState(true);

  // Filter + sort state. Lives here rather than in the query string, and
  // survives loadUsers(), so a filter stays applied after deleting a row.
  const [practiceFilter, setPracticeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'user', dir: 'asc' });
  // Which row's action menu is open, by user id. One at a time.
  const [openMenuId, setOpenMenuId] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'client', practice_access: [] });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Close the open action menu on any outside click or Escape. Bound only while
  // a menu is actually open so the page carries no listeners at rest.
  useEffect(() => {
    if (openMenuId === null) return undefined;
    const close = () => setOpenMenuId(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuId]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setLastSeenAvailable(data.last_seen_available !== false);
      } else {
        setMessage({ type: 'error', text: 'Failed to load users' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: '', name: '', role: 'client', practice_access: [] });
    setModalError(null);
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email,
      name: user.name || '',
      role: user.role,
      practice_access: (user.practice_access || []).map(a => ({
        practice_id: a.practice_id,
      })),
    });
    setModalError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setModalError(null);
    if (!editingUser && !form.email.trim()) {
      setModalError('Email is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingUser
        ? `${API_URL}/api/users/${editingUser.id}`
        : `${API_URL}/api/users`;
      const method = editingUser ? 'PUT' : 'POST';

      const body = {
        name: form.name.trim(),
        role: form.role,
        practice_access: form.practice_access,
      };
      if (!editingUser) {
        body.email = form.email.trim().toLowerCase();
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        setShowModal(false);
        setMessage({ type: 'success', text: editingUser ? 'User updated' : `Invite sent to ${body.email || form.email}` });
        loadUsers();
      } else {
        setModalError(data.error || 'Failed to save user');
      }
    } catch (err) {
      setModalError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    const action = user.is_active ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${user.email}?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !user.is_active }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `User ${user.is_active ? 'deactivated' : 'reactivated'}` });
        loadUsers();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(
      `Delete ${user.email}? This cannot be undone.\n\n`
      + `Their sign-in account is permanently deleted, including their password `
      + `and two-factor setup, any invitation they have not accepted stops working, `
      + `and their practice access is cleared.\n\n`
      + `To remove access temporarily, use Deactivate instead.`
    )) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `${user.email} deleted` });
        loadUsers();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to delete user' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    }
  };

  const handleResendInvite = async (user) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}/resend-invite`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to resend invite' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    }
  };

  // Practice access helpers
  const addPracticeAccess = () => {
    const existing = new Set(form.practice_access.map(a => a.practice_id));
    const available = (practices || []).find(p => !existing.has(p.id));
    if (available) {
      setForm({
        ...form,
        practice_access: [...form.practice_access, { practice_id: available.id }],
      });
    }
  };

  const removePracticeAccess = (index) => {
    setForm({ ...form, practice_access: form.practice_access.filter((_, i) => i !== index) });
  };

  const updatePracticeAccess = (index, field, value) => {
    const updated = [...form.practice_access];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, practice_access: updated });
  };

  const getUserStatus = (user) => {
    if (!user.is_active) return { label: 'Inactive', className: 'inactive' };
    if (!user.has_accepted_invite) return { label: 'Invited', className: 'invited' };
    return { label: 'Active', className: 'active' };
  };

  // "Never" is the only label here that makes an affirmative claim about the
  // user, so it is reserved for the one case we actually know it. An admin
  // decides who to delete from this column and delete destroys the Clerk
  // account irreversibly, so a blank must never be dressed up as a fact.
  const lastSeenLabel = (user) => {
    // The whole lookup failed. One page-wide notice already explains it.
    if (!lastSeenAvailable) return 'Unavailable';
    // Nothing to show, for either of two reasons that both mean we hold no
    // timestamp: they never accepted the invite so there is no Clerk account,
    // or Clerk did not answer for their id (which is permanent once an account
    // is deleted Clerk-side, since the webhook keeps clerk_user_id on our row).
    // Aman's call to show one glyph for both. The distinction still exists in
    // last_seen_known if it ever needs surfacing, and is load-bearing below.
    // Tested against `!== true`, not `=== false`: the backend sets the key on
    // every path today, but if it were ever renamed or dropped the row would
    // fall through to "Never" — an affirmative claim on the column that drives
    // an irreversible delete. Absent must degrade to the dash, not to a fact.
    if (!user.has_accepted_invite || user.last_seen_known !== true) return '-';
    // Clerk answered for this id and there is genuinely no timestamp.
    if (typeof user.last_seen_at !== 'number') return 'Never';
    // Clerk refreshes activity at most once a day, so the clock time is a real
    // moment but can be up to 24h behind. Rendered in the viewer's own zone.
    // The year is dropped for the current year, which is nearly every row, and
    // shown only when it would otherwise be ambiguous.
    const when = new Date(user.last_seen_at);
    const options = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    if (when.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
    return when.toLocaleString(undefined, options);
  };

  // Every row action lives behind the menu, including Edit. Built as data so the
  // trigger can be hidden entirely for a row that has none — a super admin seen
  // by a non-super-admin has no actions at all.
  const rowActions = (user) => {
    const actions = [];
    if (user.role !== 'super_admin') {
      actions.push({ label: 'Edit', onClick: () => openEdit(user) });
      actions.push({
        label: user.is_active ? 'Deactivate' : 'Reactivate',
        className: user.is_active ? 'warning' : 'success',
        onClick: () => handleToggleActive(user),
      });
      actions.push({ label: 'Delete', className: 'danger', onClick: () => handleDelete(user) });
    }
    if (!user.has_accepted_invite && user.is_active) {
      actions.push({ label: 'Resend Invite', onClick: () => handleResendInvite(user) });
    }
    return actions;
  };

  // Admins and super admins hold no practice_access rows because they reach
  // every practice; the table shows "All" for them.
  const practiceLabel = (user) => (
    (user.role === 'admin' || user.role === 'super_admin')
      ? 'All'
      : (user.practice_access || [])
          .map(a => a.practice_name || `#${a.practice_id}`)
          .join(', ')
  );

  const sortValue = (user, key) => {
    switch (key) {
      case 'email': return user.email || '';
      case 'name': return user.name || '';
      case 'role': return ROLE_LABELS[user.role] || user.role || '';
      case 'practices': return practiceLabel(user);
      case 'status': return getUserStatus(user).label;
      // The name is the line the cell leads with, so it is what the column
      // sorts on; an unnamed invite would otherwise all collapse to one value.
      case 'user': return (user.name || '').trim() || user.email || '';
      // Raw epoch, not the formatted string. Null covers every case where we
      // have no timestamp, including a failed enrichment.
      case 'last_seen':
        return typeof user.last_seen_at === 'number' ? user.last_seen_at : null;
      default: return '';
    }
  };

  const toggleSort = (key) => {
    setSort(prev => (
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ));
  };

  const practiceOptions = [...(practices || [])].sort(
    (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );

  const numericSort = SORT_COLUMNS.find(c => c.key === sort.key)?.type === 'number';

  const visibleUsers = users
    .filter(u => {
      if (statusFilter !== 'all' && getUserStatus(u).label !== statusFilter) return false;
      if (practiceFilter === 'all') return true;
      // An admin can reach every practice, so they match any practice filter.
      // Hiding them would contradict the "All" chip in their own row.
      if (u.role === 'admin' || u.role === 'super_admin') return true;
      return (u.practice_access || []).some(a => String(a.practice_id) === practiceFilter);
    })
    .sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (numericSort) {
        // Unknown sinks to the bottom in BOTH directions. A missing timestamp
        // is not "very old" and must not rank as if it were, least of all while
        // the enrichment can fail open and make every row unknown at once.
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return sort.dir === 'asc' ? av - bv : bv - av;
      }
      // String() so a future non-text column that forgets its type tag sorts
      // wrongly rather than throwing inside render — there is no ErrorBoundary
      // above this page, so a throw here is a white screen.
      const cmp = String(av).localeCompare(
        String(bv), undefined, { sensitivity: 'base' }
      );
      return sort.dir === 'asc' ? cmp : -cmp;
    });

  const filtersActive = practiceFilter !== 'all' || statusFilter !== 'all';

  const showPracticeAccess = form.role === 'biller' || form.role === 'client';

  return (
    <div className="users-section">
      <div className="tab-header">
        <div className="claims-filter-bar">
          <select
            value={practiceFilter}
            onChange={e => setPracticeFilter(e.target.value)}
          >
            <option value="all">All practices</option>
            {practiceOptions.map(p => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {filtersActive && (
            <button
              className="btn-link"
              onClick={() => { setPracticeFilter('all'); setStatusFilter('all'); }}
            >
              Clear
            </button>
          )}
        </div>
        <button className="btn btn-primary" onClick={openCreate}>Invite User</button>
      </div>

      {message && (
        <div className={`form-message ${message.type} dismissable`}>
          <span className="message-content">{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {!loading && !lastSeenAvailable && users.length > 0 && (
        <div className="form-message warning">
          <span className="message-content">
            Last Seen could not be loaded. Every other column on this page is current.
          </span>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner"></div>Loading...</div>
      ) : users.length === 0 ? (
        <div className="empty-message">No users found</div>
      ) : visibleUsers.length === 0 ? (
        <div className="empty-message">No users match these filters</div>
      ) : (
        <table className="detail-table">
          <thead>
            <tr>
              {SORT_COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="sortable"
                  onClick={() => toggleSort(col.key)}
                >
                  {col.label}
                  <span className="sort-arrow">
                    {sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
              ))}
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map(u => {
              const status = getUserStatus(u);
              const actions = rowActions(u);
              return (
                <tr key={u.id}>
                  <td className="col-user">
                    <div className="user-identity-name">{u.name || '-'}</div>
                    <div className="user-identity-email">{u.email}</div>
                  </td>
                  <td>
                    <span className={`user-role-badge ${u.role}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td>
                    {u.role === 'admin' || u.role === 'super_admin' ? (
                      <span className="user-practice-chip">All</span>
                    ) : (
                      <div className="user-practices-list">
                        {(u.practice_access || []).map(a => (
                          <span key={a.practice_id} className="user-practice-chip">
                            {a.practice_name || `#${a.practice_id}`}
                          </span>
                        ))}
                        {(!u.practice_access || u.practice_access.length === 0) && '-'}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`user-status-badge ${status.className}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className={`col-last-seen${lastSeenAvailable ? '' : ' muted'}`}>
                    {lastSeenLabel(u)}
                  </td>
                  <td className="col-actions">
                    {actions.length > 0 && (
                      <div className="user-actions">
                        <button
                          className="btn-link user-menu-trigger"
                          aria-haspopup="menu"
                          aria-expanded={openMenuId === u.id}
                          aria-label={`Actions for ${u.name || u.email}`}
                          // Stop the bubble so the document listener that closes
                          // the menu doesn't fire on the click that opened it.
                          onMouseDown={e => e.stopPropagation()}
                          onClick={() => setOpenMenuId(openMenuId === u.id ? null : u.id)}
                        >
                          &#8943;
                        </button>
                        {openMenuId === u.id && (
                          // Same stopPropagation as the trigger, and for the same
                          // reason: the document listener that closes this menu
                          // listens on mousedown, which fires BEFORE click. Without
                          // this, pressing an item closes the menu, React unmounts
                          // the button, and the click never lands — the menu just
                          // shuts and the action never runs.
                          <div
                            className="user-menu"
                            role="menu"
                            onMouseDown={e => e.stopPropagation()}
                          >
                            {actions.map(action => (
                              <button
                                key={action.label}
                                role="menuitem"
                                className={`user-menu-item${action.className ? ` ${action.className}` : ''}`}
                                onClick={() => { setOpenMenuId(null); action.onClick(); }}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Create / Edit User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="create-modal" onClick={e => e.stopPropagation()}>
            <h2>{editingUser ? 'Edit User' : 'Invite User'}</h2>
            {modalError && <div className="form-message error">{modalError}</div>}

            {!editingUser && (
              <div className="form-row">
                <div className="form-field full">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div className="form-field">
                <label>Role</label>
                <select
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                  disabled={editingUser && editingUser.role === 'super_admin'}
                >
                  {ROLE_OPTIONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Practice access for biller/client */}
            {showPracticeAccess && (
              <div className="practice-access-section">
                <h3>Practice Access</h3>
                {form.practice_access.map((access, i) => (
                  <div key={i} className="practice-access-item">
                    <select
                      value={access.practice_id}
                      onChange={e => updatePracticeAccess(i, 'practice_id', parseInt(e.target.value))}
                    >
                      {(practices || []).map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button className="remove-practice-btn" onClick={() => removePracticeAccess(i)}>&times;</button>
                  </div>
                ))}
                <button
                  className="add-practice-btn"
                  onClick={addPracticeAccess}
                  disabled={form.practice_access.length >= (practices || []).length}
                >
                  + Add Practice
                </button>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editingUser ? 'Save Changes' : 'Send Invite')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
